import { Component, h, Host, Prop, State, Watch, Fragment, Element } from '@stencil/core';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';

// Anmerkung, wenn das in dem Entry ist, ist das dann garkeine cms-component?
export interface RatingResponse {
  id: string;
  positiveRatings: string;
  rated: string;
  totalRatings: string;
}

@Component({
  tag: 'ui-cms-rating',
  styleUrl: 'ui-cms-rating.scss',
  shadow: true,
})
export class UiCmsRating {
  @Element() el: HTMLUiCmsRatingElement;

  @State() positiveRatings = 0;

  @State() totalRatings = 0;

  @State() rated = false;

  @State() error = false;

  @State() ratingButtonWidth = 0;

  @Prop() itemId: string;

  @Prop() apiKey: string;

  @Prop() apiVersion: string;

  @Prop() requestUrl: string;

  @Prop() requestUrlPositive: string;

  @Prop() requestUrlNegative: string;

  @Prop() requestUrlFeedback: string;

  @Prop() withFeedbackModal = false;

  @Prop() faq?: string;

  @Prop() url?: string;

  @Prop() targetId: string;

  @Prop() countItemLabel: string;

  @Prop() feedbackItemLabel: string;

  @Prop() additionalTrackingContent: string;

  @Prop({ mutable: true }) i18n = {
    error:
      'Aufgrund technischer Probleme ist eine Bewertung aktuell nicht möglich. Bitte versuchen Sie es später erneut.',
    yes: 'Ja',
    no: 'Nein',
    from: 'von',
    helpful: 'hilfreich',
    ratingsSingular: 'Person fand',
    ratingsPlural: 'Personen fanden',
    thanksForFeedback: 'Vielen Dank für Ihr Feedback!',
    wasFeedbackHelpful: 'War $ENTRY hilfreich für Sie?',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  uuid: string;

  private upButtonElement: HTMLUiButtonElement;

  private downButtonElement: HTMLUiButtonElement;

  private feedbackElement: HTMLUiCmsFaqFeedbackElement;

  async componentWillLoad() {
    this.uuid = Cookies.get('uuid');

    if (!this.uuid) {
      this.uuid = `RATING-${uuidv4()}`;
      Cookies.set('uuid', this.uuid, { expires: 365 * 10 });
    }

    await fetch(
      `${this.requestUrl}?id=${this.itemId}&userid=${this.uuid}&targetid=${this.targetId}&api-version=${this.apiVersion}`,
      {
        headers: {
          'x-api-key': this.apiKey,
        },
      }
    )
      .then((response) => response.json())
      .then((data: RatingResponse) => {
        this.updateState(data);
      })
      .catch((error) => {
        this.error = true;
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }

  componentDidLoad() {
    if (!this.rated) this.getWidthOfWiderButton();
  }

  getWidthOfWiderButton(): void {
    if (this.upButtonElement && this.downButtonElement) {
      const upButtonWidth = this.upButtonElement.offsetWidth;
      const downButtonWidth = this.downButtonElement.offsetWidth;
      this.ratingButtonWidth = upButtonWidth > downButtonWidth ? upButtonWidth : downButtonWidth;
    }
  }

  trackAction(isPositive: boolean) {
    window.ui?.services?.tracking?.track({
      eventInfo: {
        eventName: 'Rating Tracking',
        eventType: 'Action',
        eventCaller: this.el,
      },
      category: {
        area: 'Content',
      },
      attributes: {
        elementType: 'Button',
        elementLabel: isPositive ? 'Ja' : 'Nein',
        virtualTarget: this.additionalTrackingContent,
      },
    });
  }

  onLikeClick() {
    if (this.withFeedbackModal) {
      this.openFeedbackModal(true);
    }
    this.sendFeedback(true);
    this.trackAction(true);
  }

  onDislikeClick() {
    if (this.withFeedbackModal) {
      this.openFeedbackModal(false);
    }
    this.sendFeedback(false);
    this.trackAction(false);
  }

  updateState(data: RatingResponse) {
    this.positiveRatings = parseInt(data.positiveRatings, 10);
    this.totalRatings = parseInt(data.totalRatings, 10);
    this.rated = data.rated === 'true';
  }

  sendFeedback(isPositive: boolean) {
    const feedbackUrl = isPositive ? this.requestUrlPositive : this.requestUrlNegative;
    fetch(
      `${feedbackUrl}?id=${this.itemId}&userid=${this.uuid}&targetid=${this.targetId}&api-version=${this.apiVersion}`,
      {
        headers: {
          'x-api-key': this.apiKey,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        this.updateState(data);
      })
      .catch((error) => {
        this.error = true;
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }

  renderHeadlineText() {
    return this.rated
      ? this.i18n.thanksForFeedback
      : this.i18n.wasFeedbackHelpful.replace('$ENTRY', this.feedbackItemLabel);
  }

  getSigularOrPlural() {
    return this.totalRatings === 1 ? this.i18n.ratingsSingular : this.i18n.ratingsPlural;
  }

  openFeedbackModal(isPositive: boolean): void {
    this.feedbackElement.showFaqFeebackModal(isPositive);
  }

  render() {
    return (
      <Host>
        {this.withFeedbackModal && (
          <ui-cms-faq-feedback
            body={{
              uuid: this.uuid,
              url: this.url,
              faq: this.faq,
              requestUrlFeedback: this.requestUrlFeedback,
              apiKey: this.apiKey,
              apiVersion: this.apiVersion,
            }}
            // todo add i18
            ref={(el) => {
              this.feedbackElement = el;
            }}
          ></ui-cms-faq-feedback>
        )}
        <p class={this.error ? 'has-error' : ''}>
          {`${this.positiveRatings} ${this.i18n.from}`}
          {` ${this.totalRatings} ${this.getSigularOrPlural()} ${this.countItemLabel} ${this.i18n.helpful}`}.
        </p>
        {this.error ? (
          <p class="error">{this.i18n.error}</p>
        ) : (
          <ui-headline level={4}>{this.renderHeadlineText()}</ui-headline>
        )}
        {!this.rated && (
          <Fragment>
            <ui-button
              ref={(el) => {
                this.upButtonElement = el;
              }}
              // this.ratingButtonWidth saves num without comma f.e. 94 instead of 94.345
              style={this.ratingButtonWidth > 0 && { width: `${this.ratingButtonWidth + 1}px` }}
              icon="thumbs-up"
              onClick={() => this.onLikeClick()}
            >
              {this.i18n.yes}
            </ui-button>
            <ui-button
              ref={(el) => {
                this.downButtonElement = el;
              }}
              // this.ratingButtonWidth saves num without comma f.e. 94 instead of 94.345
              style={this.ratingButtonWidth > 0 && { width: `${this.ratingButtonWidth + 1}px` }}
              icon="thumbs-down"
              onClick={() => this.onDislikeClick()}
            >
              {this.i18n.no}
            </ui-button>
          </Fragment>
        )}
      </Host>
    );
  }
}
